import { useTranslation } from 'react-i18next'
import { Dialog, Box, IllustrationError, Text, Inline, Button } from '@machobear/design-system'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

import { useHistory } from 'react-router-dom'
import client from '@/ports/api-client'

type DeleteMessageProps = {
  trigger?: string
  visible: boolean
  contentID: string
  closeModal: () => void
}

const DeleteMessageModal = ({ visible, contentID, closeModal, trigger }: DeleteMessageProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()

  const deleteContentId = async (id: string) => {
    const response = await client.delete(`/auto_reply_trigger/content/${id}`)
    return response
  }

  const { mutate: removeMessage } = useMutation({
    mutationFn: ({ id }: { id: string }) => {
      return deleteContentId(id)
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['autoreply', 'autoreply_messages'] })
    }
  })

  const handleSubmitDeleteMessage = (id: string) => {
    removeMessage({ id })
    closeModal()
    if (trigger) history.push(`/settings/auto-reply/manage/?trigger=${trigger}`)
  }

  return (
    <Dialog
      open={visible}
      onClose={closeModal}
      title={t('l.settings.view.auto_reply.delete_message', 'Delete message?')}
      id="delete-message"
      width="xsmall"
      illustration={<IllustrationError />}
      description={<DeleteMessageDescription />}
    >
      <Inline space="gutter" align="center">
        <Button variant="transparent" onClick={closeModal}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button onClick={e => handleSubmitDeleteMessage(contentID)} tone="critical">
          {t('common.delete', 'Delete')}
        </Button>
      </Inline>
    </Dialog>
  )
}

const DeleteMessageDescription = () => {
  const { t } = useTranslation()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="spaceBetween"
      gap="large"
      marginTop={'small'}
    >
      <Text align="center">
        {t(
          'l.settings.view.auto_reply.delete_message_description',
          'This will permanently erase the message. Listings where this template is applied will no longer automatically send it.'
        )}
      </Text>
    </Box>
  )
}

export default DeleteMessageModal
